/**
 * Developed by Spell Solutions for Trivinci Systems, LLC.
 *
 * Developer attribution only. Ownership, licensing, and permitted use are
 * governed by the applicable agreement and applicable law.
 */
/**
 * Runtime configuration.
 *
 * As of the 2026-05-20 consolidation, telemetry reaches the player through
 * two channels, both terminated by the same CloudFront distribution:
 *
 *   - HLS playlists with embedded EXT-X-DATERANGE telemetry tags
 *     (default mode, synchronised with what the viewer sees on screen).
 *   - A WebSocket `/ws/telemetry` push channel proxied through CloudFront
 *     to the FastAPI on RK-Video (live engineer mode, producer rate).
 *
 * No player-side endpoint depends on `connect.race-keeper.com` any more.
 */

export const HLS_CDN_BASE = "https://d3md476ql66983.cloudfront.net";

/** WebSocket base for the live-mode push channel. Derived from HLS_CDN_BASE so
 *  the player only knows about a single AWS-managed origin. */
export const LIVE_WS_BASE = HLS_CDN_BASE.replace(/^https:\/\//, "wss://");

export const SESSIONS_API =
  "https://2dzmqbhmfj.execute-api.us-east-2.amazonaws.com/prod/sessions";

/** API key for the SESSIONS_API endpoint. The /sessions response includes
 *  per-logger IAM credentials (access_key_id / secret_access_key) used by
 *  producers to publish to KVS — so the endpoint requires `x-api-key` to
 *  prevent scraping. The key is API-Gateway-validated; client-side
 *  obfuscation isn't the security boundary. Mirrors the same constant in
 *  status.html. Rotate by updating both, then bouncing the Usage Plan. */
export const SESSIONS_API_KEY =
  "al5NvD18apjsvlFLWiAmKp_-vkegdMCJ5MxiDr6x23I";

/** HLS playlist for a given logger + camera. */
export function hlsUrl(loggerId: string, cam: "front" | "rear"): string {
  return `${HLS_CDN_BASE}/hls/${loggerId}/${cam}/index.m3u8?v=${Date.now()}`;
}

/** HLS playlist used when sourcing telemetry from EXT-X-DATERANGE attrs. */
export function hlsMetadataUrl(loggerId: string): string {
  return `${HLS_CDN_BASE}/hls/${loggerId}/front/540p/index.m3u8?t=${Date.now()}`;
}

/** Live-mode WebSocket URL (wss://) for a given logger. */
export function liveWsUrl(loggerId: string): string {
  return `${LIVE_WS_BASE}/ws/telemetry?logger_id=${encodeURIComponent(loggerId)}`;
}

export const POLL_INTERVAL_MS = 200;
export const TELEMETRY_STALE_MS = 3500;
